// ── the places a person can come back to (MOVING-IN-PLAN.md §14) ────────────
//
// Every key lib/os/resume keeps a place under, named in one file, so "where was
// i" can list them later and no two surfaces ever pick the same string. A key
// says which place, never whose: sign-out forgets them all (forgetAllResumable),
// and a browser says whose they are (claimResumable), so another person who signs
// in on it, or nobody, never reads what the last one left.
//
// The rule each surface keeps: an explicit ask (a link, a walk's focus, a
// ?view=) always wins over a kept place; the kept place is only where the person
// lands when nothing asked for somewhere else. Never memory, never a minted key.

export const PLACES = {
  /** the import sheet's staged move-in (components/connect/ImportSteps) */
  importStaged: () => 'import-sheet:staged',
  /** every tick and edit in a staged move-in's review (components/intake/Review) */
  intakeReview: (run: string) => `intake-review:${run}`,
  /** netti's unsent line in the dock (components/netti/NettiChat) */
  nettiDraft: () => 'netti:draft',
  /** the connect window's subject for one tool and connection (components/connect/ConnectGuideModal) */
  connectSection: (tool: string, connection: string | null | undefined) => `connect-guide:${tool}:${connection || 'new'}`,
  /** the name being typed for a connection not minted yet */
  connectLabel: (tool: string) => `connect-guide:${tool}:label`,
  /** a project's open view and document (components/os/OsShell) */
  project: (path: string) => `project:place:${path}`,
  /** how far a document was read (components/os/artifact) */
  reading: (path: string, page: string) => `project:read:${path}:${page}`,
  /** the settings sub-tab (app/dashboard/DashboardClient) */
  settingsTab: () => 'settings:tab',
  /** the agent make form's fields, never its key (components/agents/AgentMake) */
  agentMake: (parent: string | null | undefined) => `agent-make:${parent || 'top'}`,
  /** the docs page and how far down it was read (components/docs/DocsOsShell) */
  docs: () => 'docs:place',
} as const

/** what a place key is, by its own words, for a list of places (null: not one of these) */
export function placeOf(key: string): keyof typeof PLACES | null {
  if (key === 'import-sheet:staged') return 'importStaged'
  if (key.startsWith('intake-review:')) return 'intakeReview'
  if (key === 'netti:draft') return 'nettiDraft'
  if (/^connect-guide:[^:]+:label$/.test(key)) return 'connectLabel'
  if (key.startsWith('connect-guide:')) return 'connectSection'
  if (key.startsWith('project:place:')) return 'project'
  if (key.startsWith('project:read:')) return 'reading'
  if (key === 'settings:tab') return 'settingsTab'
  if (key.startsWith('agent-make:')) return 'agentMake'
  if (key === 'docs:place') return 'docs'
  return null
}

/**
 * Whether a project's kept place can be taken back yet (components/os/OsShell). Its level has arrived, its chips are
 * drawn, and a lazy folder opened before its pages arrived (a shallow open) has its subtree: until then it holds only
 * the few chips that need no pages, so a kept document is not found, and the view the subtree then defaults to would be
 * written over the place that was kept. Pure.
 */
export function projectPlaceReady(node: { kind: string; path: string; lazy?: boolean; children?: unknown }, path: string, chips: number): boolean {
  if (node.kind !== 'project' || node.path !== path || chips === 0) return false
  return !(node.lazy && !node.children)
}
